import { IObjectWithKey } from "office-ui-fabric-react";
import { Dictionary, EntityType } from "../../../../entities/common";
import { Resource } from "../../../../store/ResourcesListStore";
import { CalendarDataSet } from "../../../../store/calendar/common";
import { HUNDRED_PCT, roundToHundreds, toDate } from "../../../utils/common";

export type TimeType = "plan" | "actual" | "remaining" | "capacity" | "availability";
export type TimelineGroup = "resourcePlan" | "assignments";

export const timeTypesConfigMap: Dictionary<{ type: TimeType; title: string; className: string }> = {
    plan: { type: "plan", title: "Planned", className: "plan" },
    actual: { type: "actual", title: "Actual", className: "actual" },
    availability: { type: "availability", title: "Availability", className: "availability" },
    capacity: { type: "capacity", title: "Capacity", className: "capacity" }
};
export const assignmentTimeTypesConfigMap: Dictionary<{ type: TimeType; title: string; className: string }> = {
    plan: { type: "plan", title: "Effort", className: "plan" },
    actual: { type: "actual", title: "Completed Work", className: "actual" },
    remaining: { type: "remaining", title: "Remaining Work", className: "remaining" },
    availability: { type: "availability", title: "Availability", className: "availability" },
    capacity: { type: "capacity", title: "Capacity", className: "capacity" }
};
export const allTimeTypes: TimeType[] = ["plan", "actual", "remaining", "availability", "capacity"];
export const defaultTimeType: TimeType[] = ["plan"];

export type HoursData = {
    capacity: number;
    plan: number;
    actual: number;
    remaining: number;
    availability: number;
    planPercent: number | undefined;
    actualPercent: number | undefined;
    remainingPercent: number | undefined;
    availabilityPercent: number | undefined;
    planFte: number | undefined;
    actualFte: number | undefined;
    remainingFte: number | undefined;
    availabilityFte: number | undefined;
};

export type CellSummaryData = {
    hours: HoursData;
    fullFteCapacityHours: number;
    hasOverage: boolean;
    hasLocalOverage: boolean;
    hasProposedOverage: boolean;
    hasLocalProposedOverage: boolean;
};

export type CellSummaryContext = {
    resource: Resource;
    calendar: CalendarDataSet;
    entityType: EntityType;
    resourcePlanningLevel: EntityType;
    defaultCostRateTableId?: string;
    entityId?: string;
};

export interface ISelectableCellData extends IObjectWithKey {
    entityId: string;
    start: Date;
    finish: Date;
}

export type AssignmentsWork = {
    effort: number;
    completedWork: number;
    remainingWork: number;
}

export type ServerAssignmentsByDay = AssignmentsWork & {
    date: string;
}

export const toAssignmentsByDay = (byDay: ServerAssignmentsByDay[]): Dictionary<AssignmentsWork> =>
    byDay.reduce<Dictionary<AssignmentsWork>>((acc, _) => {
        acc[toDate(_.date)!.getBeginOfDay().toISOString()] = { effort: _.effort, completedWork: _.completedWork, remainingWork: _.remainingWork };
        return acc;
    }, {});

export const toHoursData = (
    plan: number,
    actual: number,
    remaining: number,
    capacity: number,
    availability: number,
    fullFteCapacityHours: number
): HoursData => ({
    plan,
    actual,
    remaining,
    capacity,
    availability,
    planPercent: capacity ? Math.round((plan / capacity) * HUNDRED_PCT) : undefined,
    actualPercent: capacity ? Math.round((actual / capacity) * HUNDRED_PCT) : undefined,
    remainingPercent: capacity ? Math.round((remaining / capacity) * HUNDRED_PCT) : undefined,
    availabilityPercent: capacity ? Math.round((availability / capacity) * HUNDRED_PCT) : undefined,
    planFte: fullFteCapacityHours ? roundToHundreds(plan / fullFteCapacityHours) : undefined,
    actualFte: fullFteCapacityHours ? roundToHundreds(actual / fullFteCapacityHours) : undefined,
    remainingFte: fullFteCapacityHours ? roundToHundreds(remaining / fullFteCapacityHours) : undefined,
    availabilityFte: fullFteCapacityHours ? roundToHundreds(availability / fullFteCapacityHours) : undefined
});
